import { FC } from 'react';
import { Box, Link, Skeleton, Text } from '@hopskipdrive/react-components';
import PhoneEnabledIcon from '@mui/icons-material/PhoneEnabled';
import styled from 'styled-components';

import { ContactCard } from '../../../shared/components/ContactCard';
import { CopyToClipboard } from '../../../shared/components/CopyToClipboard';
import { colorPalette, colorTokens } from '../../../shared/components/variables';

interface CarePartnerCardProps {
  isFetching?: boolean;
  name?: string;
  url?: string;
  certifiedDriversCount?: number;
  phoneNumber?: string;
}

export const CarePartnerCard: FC<CarePartnerCardProps> = ({
  name,
  url,
  certifiedDriversCount,
  phoneNumber,
  isFetching = false,
}) => {
  return isFetching ? (
    <SkeletonCarePartnerCard />
  ) : (
    <CardWrapper>
      {name ? (
        <ContactCard color={colorPalette.plum200}>
          <ContactCard.Title>
            <Link
              target="_blank"
              variant="h6"
              underline="hover"
              href={url}
              style={{ color: colorPalette.plum200, fontWeight: 500 }}
            >
              {`CarePartner: ${name}`}
            </Link>
          </ContactCard.Title>
          <ContactCard.Content>
            <Box display="flex" flexDirection="column" gap="6px">
              <Text variant="subtitle1" textColor="secondaryText">
                Certified Drivers
              </Text>
              <Text variant="h6" style={{ color: colorTokens.text.primary }}>
                {certifiedDriversCount}
              </Text>
            </Box>
            {phoneNumber && (
              <Box display="flex" flexDirection="row" alignItems="center" gap="12px">
                <PhoneEnabledIcon style={{ color: colorTokens.text.secondary }} />
                <CopyToClipboard
                  toastMessage="Phone Number Copied"
                  text={phoneNumber}
                  variant="h6"
                  color={colorTokens.text.secondary}
                />
              </Box>
            )}
          </ContactCard.Content>
        </ContactCard>
      ) : (
        <Text variant="subtitle1" textColor="secondaryText" padding="4px 8px">
          No CarePartner
        </Text>
      )}
    </CardWrapper>
  );
};

const CardWrapper = styled(Box)`
  display: flex;
  padding: 12px;
  border-radius: 12px;
  border: 1px solid ${colorTokens.border.main};
  background: ${colorPalette.plum975};
  width: 100%;
`;

const SkeletonCarePartnerCard: FC<CarePartnerCardProps> = () => {
  return (
    <CardWrapper>
      <ContactCard color={colorPalette.plum200}>
        <ContactCard.Title>
          <Skeleton variant="text" width={255} height={32} />
        </ContactCard.Title>
        <ContactCard.Content>
          <Box display="flex" flexDirection="column" gap="6px">
            <Text variant="subtitle1" textColor="secondaryText">
              Certified Drivers
            </Text>
            <Skeleton variant="text" width={24} height={32} />
          </Box>

          <Box display="flex" flexDirection="row" alignItems="center" gap="12px">
            <Skeleton data-testid="care-partner-skeleton-loader" variant="text" width={24} height={32} />
            <Skeleton variant="text" width={129} height={32} />
            <Skeleton variant="text" width={24} height={32} />
          </Box>
        </ContactCard.Content>
      </ContactCard>
    </CardWrapper>
  );
};
